import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar, Trash2, Plus, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AvailabilityCalendar = ({ userId, editable = false }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newSlot, setNewSlot] = useState({ start_date: '', end_date: '', note: '' });

  const fetchSlots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/availability/${userId}`);
      setSlots(res.data || []);
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchSlots();
  }, [userId, fetchSlots]);

  const handleAdd = async () => {
    if (!newSlot.start_date || !newSlot.end_date) {
      toast.error('Veuillez remplir les dates de début et de fin');
      return;
    }
    if (newSlot.start_date > newSlot.end_date) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/availability`, newSlot, { withCredentials: true });
      toast.success('Disponibilité ajoutée');
      setNewSlot({ start_date: '', end_date: '', note: '' });
      fetchSlots();
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (slotId) => {
    try {
      await axios.delete(`${API}/availability/${slotId}`, { withCredentials: true });
      toast.success('Créneau supprimé');
      setSlots(prev => prev.filter(s => s.id !== slotId));
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const isUpcoming = (dateStr) => new Date(dateStr) >= new Date();

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1E3A8A]" />
        </CardContent>
      </Card>
    );
  }

  const upcomingSlots = slots.filter(s => isUpcoming(s.end_date));
  const pastSlots = slots.filter(s => !isUpcoming(s.end_date));

  return (
    <Card data-testid="availability-calendar">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#1E3A8A]" />
          Calendrier de disponibilité
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {editable && (
          <div className="p-4 bg-[#F9FAFB] rounded-lg space-y-3" data-testid="add-availability-form">
            <p className="text-sm font-medium text-[#374151]">Ajouter une période de disponibilité</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="avail-start">Date de début</Label>
                <Input
                  id="avail-start"
                  type="date"
                  value={newSlot.start_date}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, start_date: e.target.value }))}
                  className="input-field"
                  data-testid="availability-start-date"
                />
              </div>
              <div>
                <Label htmlFor="avail-end">Date de fin</Label>
                <Input
                  id="avail-end"
                  type="date"
                  value={newSlot.end_date}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, end_date: e.target.value }))}
                  className="input-field"
                  data-testid="availability-end-date"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="avail-note">Note (optionnel)</Label>
              <Input
                id="avail-note"
                placeholder="Ex: Disponible pour chantiers en région bruxelloise"
                value={newSlot.note}
                onChange={(e) => setNewSlot(prev => ({ ...prev, note: e.target.value }))}
                className="input-field"
                data-testid="availability-note"
              />
            </div>
            <Button 
              onClick={handleAdd} 
              disabled={adding} 
              className="btn-primary gap-2"
              data-testid="add-availability-button"
            >
              <Plus className="w-4 h-4" />
              {adding ? 'Ajout...' : 'Ajouter'}
            </Button>
          </div>
        )}

        {upcomingSlots.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-[#374151]">Périodes disponibles</p>
            {upcomingSlots.map((slot) => (
              <div 
                key={slot.id} 
                className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-lg"
                data-testid={`availability-slot-${slot.id}`}
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">
                      {formatDate(slot.start_date)} — {formatDate(slot.end_date)}
                    </p>
                    {slot.note && <p className="text-xs text-emerald-600">{slot.note}</p>}
                  </div>
                </div>
                {editable && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDelete(slot.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-slot-${slot.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-[#6B7280]">
            <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">
              {editable 
                ? 'Aucune période de disponibilité définie. Ajoutez vos créneaux ci-dessus.' 
                : 'Aucune disponibilité renseignée'}
            </p>
          </div>
        )}

        {pastSlots.length > 0 && (
          <details className="text-sm">
            <summary className="text-[#6B7280] cursor-pointer hover:text-[#374151]">
              Historique ({pastSlots.length} périodes passées)
            </summary>
            <div className="space-y-2 mt-2">
              {pastSlots.map((slot) => (
                <div key={slot.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded opacity-60">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {formatDate(slot.start_date)} — {formatDate(slot.end_date)}
                    {slot.note && ` · ${slot.note}`}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailabilityCalendar;
